import { COBClientDetail } from "creso-core/types";
import { Download, Loader2 } from "lucide-react";
import {
  Button,
  cn,
  TooltipContent,
  TooltipTrigger,
  Tooltip,
} from "web-design-system";
import { useState } from "react";
import { useDownloadPdfReport } from "web-core/hooks";
import { getPdfDownloadUrl } from "web-core/utils";
import { useInvalidateCobBreakdown } from "creso-core/hooks";
import { useCob } from "../providers/cob-provider";
import { ConfirmCobTransit } from "./confirm-transit-dialog";

export function CobClientActionCell({ client }: { client: COBClientDetail }) {
  const { initiateCobFormDownload, setSelectedClient } = useCob();

  const [downloadReport, isGenerating] = useDownloadPdfReport();

  const canDownload = client.folio_details.some((fd) =>
    ["DOWNLOADED", "IDLE", "REJECTED"].includes(fd.status)
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const invalidateCobBreakdown = useInvalidateCobBreakdown();

  const handleDownload = async () => {
    try {
      const foliosToDownload = client.folio_details.filter((fd) =>
        ["DOWNLOADED", "REJECTED"].includes(fd.status)
      );
      setIsDownloading(true);
      await initiateCobFormDownload(client, foliosToDownload);
      await downloadReport(
        getPdfDownloadUrl(`/reports/clients/${client?.client_id}/cob`),
        `${client?.client_name}-change-of-broker.pdf`,
        {
          loading: "Generating change of broker form",
        }
      );
      invalidateCobBreakdown();
      setSelectedClient((prev) => {
        if (!prev) return prev;
        const newClient = { ...prev };
        newClient.folio_details = newClient.folio_details.map((fd) => {
          if (foliosToDownload.some((f) => f.folio === fd.folio)) {
            return { ...fd, status: "DOWNLOADED" };
          }
          return fd;
        });
        return newClient;
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-row gap-2">
      <ConfirmCobTransit client={client} />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            disabled={isGenerating || isDownloading}
            className={cn("hidden", canDownload && "inline-flex")}
            variant="ghost"
            size="icon"
            onClick={handleDownload}
          >
            {isDownloading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Download />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Download again</TooltipContent>
      </Tooltip>
      <Button
        variant="link"
        className="p-0 h-auto ml-auto"
        onClick={() => setSelectedClient(client)}
      >
        View Details
      </Button>
    </div>
  );
}
